/** @jsxImportSource @emotion/react */

import { Pencil, Plus, SquareArrowOutUpRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';
import { history } from 'store/store';
import { Pages } from 'modules/navigation/domain/enums/pages';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import EmptyButton from 'ud-ui/components/empty-button';
import backgroundElems from 'ud-ui/components/fabric-canvas/addons/background-elems';
import useFabric from 'ud-ui/components/fabric-canvas/hooks/use-fabric';
import BoardItem from 'ud-ui/components/fabric-canvas/view-boards/boards-list/board-item';
import BoardsList from 'ud-ui/components/fabric-canvas/view-boards/boards-list/boards-list';
import UdHeaderViewBoards from 'ud-ui/components/fabric-canvas/view-boards/right/ud-header-view-boards';
import { CanvasWrapper, Container, LeftContainer, RightContainer } from 'ud-ui/components/fabric-canvas/view-boards/styles';
import { useMainLayout } from 'ud-ui/layout/main/context';
import { setActiveObjects, setHasChanges, setSavedScheme } from '../../store/editor/editor';
import { setActiveBoard } from '../../store/templates/templates';
import LeftMenuSkeleton from '../components/skeletons/left-menu-skeleton';
import useCreateModal from '../hooks/modals/board/create';
import useBoards from '../hooks/use-boards';
import { useLoadTemplates } from '../hooks/use-load-templates';
import useLocalStorage from '../hooks/use-local-storage';
import useTemplates from '../hooks/use-templates';
import { MainBlock } from './styles';
import { MANUAL } from 'shared/constants/links';

function Boards() {
  const dispatch = useDispatch();
  const [isInit, setInit] = useState<boolean>(false);
  const { activeTemplate } = useTemplates();
  const { boards, activeBoard, activeScheme, isLoading } = useBoards();
  const { canvas, manager } = useFabric('static', {
    data: { ...activeTemplate?.resolution },
    style: { borderRadius: 8, position: 'absolute' },
    resizing: true,
  });
  const { setHeaderContent } = useMainLayout();

  useLoadTemplates();

  const { clearData } = useLocalStorage();

  useEffect(() => {
    clearData();
    dispatch(setActiveObjects([]));
    dispatch(setSavedScheme(undefined));
    dispatch(setHasChanges(false));
  }, [clearData, dispatch]);

  useEffect(() => {
    if (!boards.length) {
      return;
    }
    if (!activeBoard || !activeTemplate || !boards.find((board) => board.id === activeBoard.id)) {
      dispatch(setActiveBoard({ id: boards[0].id }));
    }
  }, [activeTemplate, activeBoard, boards, dispatch]);

  const isEditable = useMemo(() => {
    if (!activeTemplate) {
      return false;
    }
    return activeTemplate.access !== 'default';
  }, [activeTemplate]);

  useEffect(() => {
    if (!manager) {
      return;
    }
    backgroundElems.init(manager.canvas);
    setInit(true);
  }, [manager]);

  useEffect(() => {
    if (!activeTemplate) {
      return;
    }
    manager?.setResolution(activeTemplate?.resolution);
    manager?.normalizedViewport();
  }, [activeTemplate, manager]);

  useEffect(() => {
    if (!manager) {
      return;
    }
    if (!activeScheme) {
      return;
    }
    manager.loadLocal({ scheme: activeScheme, safeBackground: true }).then(() => {
      manager.normalizedViewport();
    });
  }, [activeBoard, activeScheme, manager]);

  const onGoEdit = useCallback(() => {
    if (!isInit) {
      return;
    }
    if (!activeTemplate) {
      return;
    }
    if (!activeBoard) {
      return;
    }
    if (!isEditable) {
      return;
    }

    history.push(Pages.TEMPLATE.builders.editor(activeTemplate.id, activeBoard.id), {
      from: 'boards',
    });
  }, [activeBoard, activeTemplate, isEditable, isInit]);

  const breadcrumb = useMemo(() => {
    return (
      <UDBreadcrumbs
        className="my-10"
        breadcrumbs={[
          { label: 'Турниры', to: Pages.TOURNEYS.INDEX },
          { label: 'Шаблоны', to: Pages.TEMPLATE.INDEX },
          { label: activeTemplate?.name ?? 'Табло', to: Pages.TEMPLATE.BOARDS },
        ]}
      />
    );
  }, [activeTemplate?.name]);

  const { modal: createModal, open: openCreateModal } = useCreateModal();
  const redirectToManual = useCallback(() => {
    window.open(MANUAL, '_blank');
  }, []);

  useEffect(() => {
    setHeaderContent(breadcrumb);
    return () => setHeaderContent(null);
  }, [breadcrumb, setHeaderContent]);

  return (
    <>
      <MainBlock>
        <Container style={{ opacity: isLoading ? 0.5 : 1 }}>
          {isLoading ? (
            <LeftMenuSkeleton showAddNewBoard={isEditable} />
          ) : (
            <LeftContainer>
              <BoardsList
                items={boards.map((board) => {
                  return (
                    <BoardItem
                      key={board.id}
                      board={board}
                      isEditable={isEditable || boards.length <= 1}
                      onClick={() => dispatch(setActiveBoard({ id: board.id }))}
                      isActive={board.id === activeBoard?.id}
                    />
                  );
                })}
                headerText={'Табло'}
              />
              {isEditable && (
                <EmptyButton disabled={isLoading} onClick={openCreateModal} color={'#A2A2A2'} icon={<Plus size={16} />}>
                  Добавить табло
                </EmptyButton>
              )}
            </LeftContainer>
          )}

          <RightContainer>
            <UdHeaderViewBoards
              mainText={activeBoard?.name}
              buttons={[
                <EmptyButton key={'edit'} disabled={!isEditable} onClick={onGoEdit} icon={<Pencil size={20} />}>
                  Редактировать
                </EmptyButton>,
              ]}
              subElems={[
                <EmptyButton key={'manual'} onClick={redirectToManual} icon={<SquareArrowOutUpRight size={18} />}>
                  Инструкция
                </EmptyButton>,
              ]}
            />
            <CanvasWrapper>{canvas}</CanvasWrapper>
          </RightContainer>
        </Container>
      </MainBlock>
      {createModal}
    </>
  );
}

export default Boards;
